import React from "react"
import { useRouter } from "next/router"
import {
  getCarsMakeModelYearUrl,
  getCarsMakeModelYearTrimUrl,
} from "@utils/urls"
import VehicleSection from "./VehicleSection"
import { useUpholsteryCarsForCarSelector } from "./hooks/useUpholsteryCarsForCarSelector"
import { useSaveVehicleToStore } from "./hooks/useSaveVehicleToStore"
import { useVehicleSelectionHandlers } from "./hooks/useVehicleSelectionHandlers"
import useGtmEvents from "@hooks/useGtmEvents"
import { isPdmStructureV2Enabled } from "@views/UpholsteryV2"
import { buildUpholsteryLandingHref } from "@views/InventoryChecker/helpers"

// Popular builds data for modern vehicles
const modernPopularBuilds = [
  {
    label: "2024 Chevrolet Silverado 1500",
    make: "chevrolet",
    model: "silverado-1500",
    year: "2024",
  },
  {
    label: "2024 Ford F-150",
    make: "ford",
    model: "f-150",
    year: "2024",
  },
  {
    label: "2024 Toyota Tacoma",
    make: "toyota",
    model: "tacoma",
    year: "2024",
  },
]

// Helper function to determine if a car is classic or modern based on years
const isClassicCar = (car: any): boolean => {
  if (!car.years) return true
  const yearList = car.years.split(", ").map((y: string) => parseInt(y.trim()))
  const maxYear = Math.max(...yearList.filter((y: number) => !isNaN(y)))
  return maxYear < 2020
}

export interface ModernVehicleSelectorProps {
  cars: any[]
  module?: string
  onRequestCloseMenu?: () => void
  resetPicker?: boolean
  onResetPicker?: () => void
  showPopularBuilds?: boolean
}

const ModernVehicleSelector: React.FC<ModernVehicleSelectorProps> = ({
  cars,
  module = "automotive",
  onRequestCloseMenu,
  resetPicker,
  onResetPicker,
  showPopularBuilds = true,
}) => {
  const router = useRouter()
  const { onVehicleSelectInteraction } = useGtmEvents()
  const isV2 = isPdmStructureV2Enabled()

  // PDM catalog load + refetch is owned by `useGlobalVehicleCatalogLoader` in Layout.
  const {
    modernCars: upholsteryModernCars,
    isUsingUpholsteryCars,
    upholsteryCars,
    catalogLoading,
  } = useUpholsteryCarsForCarSelector()

  // Hook to save vehicle selection to store
  const { saveVehicle } = useSaveVehicleToStore()

  const filteredCars = cars.filter((car) => car.module == module)

  const modernCars = React.useMemo(() => {
    if (isV2) {
      return upholsteryModernCars
    }
    if (upholsteryModernCars.length > 0) {
      return upholsteryModernCars
    }
    return filteredCars.filter((car) => !isClassicCar(car))
  }, [isV2, upholsteryModernCars, filteredCars])

  // Modern cars state
  const [modernMake, setModernMake] = React.useState<string>("")
  const [modernModel, setModernModel] = React.useState<string>("")
  const [modernTrim, setModernTrim] = React.useState<string>("")
  const [modernYear, setModernYear] = React.useState<string>("")
  const [modernModels, setModernModels] = React.useState<Array<any>>([])
  const [modernTrims, setModernTrims] = React.useState<Array<any>>([])
  const [modernYears, setModernYears] = React.useState<Array<any>>([])
  const [modernLoading, setModernLoading] = React.useState<boolean>(false)

  // Modern vehicle selection handlers
  const {
    makes: modernMakes,
    onSelectMake: onModernSelectMakeBase,
    onSelectModel: onModernSelectModelBase,
    onSelectTrim: onModernSelectTrimBase,
  } = useVehicleSelectionHandlers({
    cars: modernCars,
    selectedMake: modernMake,
    selectedModel: modernModel,
    selectedTrim: modernTrim,
    setMake: setModernMake,
    setModel: setModernModel,
    setTrim: setModernTrim,
    setYear: setModernYear,
    setModels: setModernModels,
    setTrims: setModernTrims,
    setYears: setModernYears,
    isModern: true,
  })

  const onModernSelectMake = (value: string) => {
    onModernSelectMakeBase(value)
    if (value) {
      onVehicleSelectInteraction("vehicle_select_make", {
        flow: "shop_by_vehicle",
        source: "homepage",
        vehicle_type: "modern",
        make: value,
      })
    }
  }

  const onModernSelectModel = (value: string) => {
    onModernSelectModelBase(value)
    if (value) {
      onVehicleSelectInteraction("vehicle_select_model", {
        flow: "shop_by_vehicle",
        source: "homepage",
        vehicle_type: "modern",
        make: modernMake,
        model: value,
      })
    }
  }

  const onModernSelectTrim = (value: string) => {
    onModernSelectTrimBase(value)
    if (value) {
      onVehicleSelectInteraction("vehicle_dropdown_open", {
        flow: "shop_by_vehicle",
        source: "homepage",
        vehicle_type: "modern",
        field: "trim",
      })
    }
  }

  const handleModernBuild = async () => {
    // If using upholstery cars, save the selection to the store and redirect to upholstery
    if (isUsingUpholsteryCars && modernMake && modernModel && modernYear) {
      setModernLoading(true)
      try {
        const success = await saveVehicle(modernMake, modernModel, modernYear)

        if (success) {
          // Close menu before navigation
          if (onRequestCloseMenu) {
            onRequestCloseMenu()
          }
          window.scrollTo({ top: 20, behavior: "smooth" })
          onVehicleSelectInteraction("vehicle_click_build", {
            flow: "shop_by_vehicle",
            source: "homepage",
            vehicle_type: "modern",
            make: modernMake,
            model: modernModel,
            year: modernYear,
            ...(modernTrim ? { trim: modernTrim } : {}),
          })
          // Navigate to inventory landing with YMM preselected
          router.push(
            buildUpholsteryLandingHref(modernMake, modernModel, modernYear)
          )
          return
        }

        // Modern/upholstery vehicles must not fall back to /cars/... —
        // those URLs often have no classic product page and crash the app.
        if (process.env.NODE_ENV === "development") {
          console.warn(
            `Could not load upholstery config for ${modernMake}/${modernModel}/${modernYear}. Not redirecting to /cars.`
          )
        }
        return
      } finally {
        setModernLoading(false)
      }
    }

    // Fallback: classic/layout cars only (not upholstery modern flow)
    if (onRequestCloseMenu) {
      onRequestCloseMenu()
    }
    window.scrollTo({ top: 20, behavior: "smooth" })

    if (modernMake && modernModel && modernYear) {
      onVehicleSelectInteraction("vehicle_click_build", {
        flow: "shop_by_vehicle",
        source: "homepage",
        vehicle_type: "modern",
        make: modernMake,
        model: modernModel,
        year: modernYear,
        ...(modernTrim ? { trim: modernTrim } : {}),
      })
    }

    if (modernTrim) {
      router.push(
        getCarsMakeModelYearTrimUrl(
          modernMake,
          modernModel,
          modernYear,
          modernTrim
        )
      )
    } else {
      router.push(getCarsMakeModelYearUrl(modernMake, modernModel, modernYear))
    }
  }

  const handlePopularBuildClick = async (build: any) => {
    // If using upholstery cars, save to store and redirect to upholstery
    if (isUsingUpholsteryCars && upholsteryCars.length > 0) {
      setModernLoading(true)
      try {
        const success = await saveVehicle(build.make, build.model, build.year)

        if (success) {
          // Close menu before navigation
          if (onRequestCloseMenu) {
            onRequestCloseMenu()
          }
          window.scrollTo({ top: 20, behavior: "smooth" })
          onVehicleSelectInteraction("vehicle_click_popular_build", {
            flow: "shop_by_vehicle",
            source: "homepage",
            vehicle_type: "modern",
            make: build.make,
            model: build.model,
            year: build.year,
            label: build.label,
          })
          router.push(
            buildUpholsteryLandingHref(build.make, build.model, build.year)
          )
          return
        }

        if (process.env.NODE_ENV === "development") {
          console.warn(
            `Could not load upholstery config for ${build.make}/${build.model}/${build.year}. Not redirecting to /cars.`
          )
        }
        return
      } catch (error) {
        if (process.env.NODE_ENV === "development") {
          console.error("Error saving vehicle to store:", error)
        }
        return
      } finally {
        setModernLoading(false)
      }
    }

    // Fallback: only when not using upholstery modern cars
    if (onRequestCloseMenu) {
      onRequestCloseMenu()
    }
    window.scrollTo({ top: 20, behavior: "smooth" })
    const year_type = build.type ? `${build.year}/${build.type}` : build.year
    onVehicleSelectInteraction("vehicle_click_popular_build", {
      flow: "shop_by_vehicle",
      source: "homepage",
      vehicle_type: "modern",
      make: build.make,
      model: build.model,
      year: build.year,
      label: build.label,
    })
    router.push(getCarsMakeModelYearUrl(build.make, build.model, year_type))
  }

  const reset = () => {
    setModernMake("")
    setModernModel("")
    setModernTrim("")
    setModernYear("")
    setModernModels([])
    setModernTrims([])
    setModernYears([])
  }

  React.useEffect(() => {
    if (resetPicker) {
      reset()
      if (onResetPicker) {
        onResetPicker()
      }
    }
  }, [resetPicker])

  return (
    <VehicleSection
      title="2018 - Present"
      variant="modern"
      makes={modernMakes}
      models={modernModels}
      trims={modernTrims}
      years={modernYears}
      selectedMake={modernMake}
      selectedModel={modernModel}
      selectedTrim={modernTrim}
      selectedYear={modernYear}
      onSelectMake={onModernSelectMake}
      onSelectModel={onModernSelectModel}
      onSelectTrim={onModernSelectTrim}
      onSelectYear={setModernYear}
      onBuild={handleModernBuild}
      popularBuilds={modernPopularBuilds}
      onPopularBuildClick={handlePopularBuildClick}
      loading={modernLoading || catalogLoading}
      catalogLoading={catalogLoading}
      showPopularBuilds={showPopularBuilds}
    />
  )
}

export default ModernVehicleSelector
